import { readSaTokenHeader, USER_PUBLIC_ID_HEADER } from '@glyphic/shared-core'

import { createLogger } from '../lib/logger'
import { randomUuid } from '../lib/randomUuid'
import { getAuthToken } from '../lib/session'
import { readStoredUserPublicId } from '../lib/userPublicId'
import type {
  RequestInterceptor,
  ResponseInterceptor,
} from './httpClient'

const apiLogger = createLogger('api')

/** 统一声明可接受 JSON */
export const attachJsonAccept: RequestInterceptor = async ({ init }) => {
  const h = new Headers(init.headers)
  if (!h.has('Accept')) {
    h.set('Accept', 'application/json')
  }
  init.headers = h
}

/** 便于后端日志串联（无则生成） */
export const attachRequestId: RequestInterceptor = async ({ init }) => {
  const h = new Headers(init.headers)
  if (!h.has('X-Request-Id')) {
    h.set('X-Request-Id', randomUuid())
  }
  init.headers = h
}

export const attachClientMeta: RequestInterceptor = async ({ init }) => {
  const h = new Headers(init.headers)
  if (!h.has('X-Client-App')) {
    h.set('X-Client-App', 'glyphicai-web')
  }
  init.headers = h
}

function userTokenHeaderName(): string {
  return readSaTokenHeader(
    () => import.meta.env.VITE_SA_TOKEN_HEADER as string | undefined,
    { dev: import.meta.env.DEV },
  )
}

/** 用户端 Sa-Token 请求头（dev→satoken，prod→Authorization，与后端 sa-token.token-name 对齐） */
export const attachSaToken: RequestInterceptor = async ({ init }) => {
  const token = getAuthToken()
  if (!token) return
  const headerName = userTokenHeaderName()
  const h = new Headers(init.headers)
  if (!h.has(headerName)) {
    h.set(headerName, token)
  }
  init.headers = h
}

/**
 * 已登录时在请求中带 `public_id`，与后端 `satoken` 解析一致时可省略，但写入便于对齐日志。
 * 未登录不传该头（避免沿用本地占位 UUID 绕过鉴权）。
 */
export const attachUserPublicId: RequestInterceptor = async ({ init }) => {
  const token = getAuthToken()
  if (!token) return

  const h = new Headers(init.headers)
  if (!h.has(USER_PUBLIC_ID_HEADER)) {
    const pid = readStoredUserPublicId()
    if (pid) {
      h.set(USER_PUBLIC_ID_HEADER, pid)
    }
  }
  init.headers = h
}

/** 开发环境打印响应摘要，生产无输出 */
export const logResponseInDev: ResponseInterceptor = async (ctx) => {
  if (!import.meta.env.DEV) return
  const snippet =
    ctx.rawBody.length > 280 ? `${ctx.rawBody.slice(0, 280)}…` : ctx.rawBody
  apiLogger.debug(
    '响应',
    ctx.response.status,
    ctx.path,
    ctx.parsed ?? snippet,
  )
}
